import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, CheckCircle, Clock, MapPin, Users, Wrench, Star, Shield } from 'lucide-react';

const MobileServiceSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % features.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: Clock,
      title: 'Service en 30 minutes',
      description: 'Intervention rapide et efficace',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Shield,
      title: 'Huiles Shell premium',
      description: 'Qualité garantie pour votre moteur',
      color: 'from-yellow-500 to-orange-500'
    },
    {
      icon: MapPin,
      title: 'Partout à Casablanca',
      description: 'Service mobile dans toute la ville',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Star,
      title: 'Techniciens certifiés',
      description: 'Professionnels expérimentés',
      color: 'from-purple-500 to-indigo-600'
    }
  ];

  const targetAudiences = [
    {
      icon: Users,
      title: 'Particuliers pressés',
      description: 'Gagnez du temps, nous venons à vous'
    },
    {
      icon: Wrench,
      title: 'Conducteurs professionnels',
      description: 'Maintenez votre véhicule en parfait état'
    },
    {
      icon: MapPin,
      title: 'Entreprises avec flotte',
      description: 'Gestion simplifiée de votre parc automobile'
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-br from-blue-50 via-white to-indigo-50 relative overflow-hidden"
      itemScope
      itemType="https://schema.org/Service"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-200/30 rounded-full animate-pulse" />
        <div className="absolute bottom-20 right-10 w-24 h-24 bg-indigo-200/30 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/4 w-40 h-40 bg-cyan-100/40 rounded-full blur-xl" />
        
        {/* Floating Icons */}
        <div className="absolute inset-0 opacity-5">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            >
              🔧
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-4">
            <Wrench className="w-4 h-4 mr-2" />
            Service mobile innovant
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              Atelier mobile
            </span>{' '}
            – Vidange à domicile
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Faites votre vidange sans vous déplacer. Notre technicien équipé se rend chez vous 
            avec des huiles Shell Helix et des filtres premium.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Content Side */}
          <div className={`space-y-8 transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            
            {/* Main Description */}
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Wrench className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" itemProp="name">
                    Service professionnel à votre porte
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed" itemProp="description">
                    Notre équipe se déplace avec tout l'équipement nécessaire pour réaliser 
                    votre vidange dans les meilleures conditions, rapidement et proprement.
                  </p>
                </div>
              </div>

              {/* Shell Partnership Highlight */}
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6 border border-yellow-200">
                <div className="flex items-center space-x-4">
                  <img 
                    src="/images/shell.png" 
                    alt="Shell" 
                    className="w-12 h-12 rounded-full"
                    loading="lazy"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900">Partenaire officiel Shell</h4>
                    <p className="text-gray-600">Huiles Helix premium pour une protection optimale</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Rotating Features */}
            <div className="space-y-4">
              <h4 className="text-xl font-bold text-gray-900">Pourquoi choisir notre service mobile ?</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  const isActive = index === activeFeature;
                  
                  return (
                    <div
                      key={index}
                      className={`
                        p-4 rounded-xl border transition-all duration-500 cursor-pointer
                        ${isActive 
                          ? 'bg-gradient-to-r ' + feature.color + ' text-white shadow-lg scale-105' 
                          : 'bg-white border-gray-200 hover:border-blue-300'
                        }
                      `}
                      onMouseEnter={() => setActiveFeature(index)}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon className={`w-6 h-6 ${isActive ? 'text-white' : 'text-blue-600'}`} />
                        <div>
                          <h5 className={`font-semibold ${isActive ? 'text-white' : 'text-gray-900'}`}>
                            {feature.title}
                          </h5>
                          <p className={`text-sm ${isActive ? 'text-white/90' : 'text-gray-600'}`}>
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Target Audiences */}
            <div className="space-y-4">
              <h4 className="text-xl font-bold text-gray-900">Destiné à :</h4>
              <div className="space-y-3">
                {targetAudiences.map((audience, index) => {
                  const Icon = audience.icon;
                  return (
                    <div 
                      key={index}
                      className={`
                        flex items-center space-x-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100
                        transition-all duration-500 hover:shadow-md hover:scale-105
                        ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}
                      `}
                      style={{ transitionDelay: `${(index + 3) * 200}ms` }}
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900">{audience.title}</h5>
                        <p className="text-sm text-gray-600">{audience.description}</p>
                      </div>
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    </div>
                  );
                })}
              </div>
            </div>


          </div>

          {/* Visual Side */}
          <div className={`relative transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            
            {/* Main Van Image */}
            <div className="relative">
              <div className="relative bg-gradient-to-br from-blue-100 to-indigo-100 rounded-3xl p-8 pr-0 shadow-2xl">
                <img
                  src="/images/faucon_bleu.png"
                  alt="Véhicule atelier mobile Faucon Bleu"
                  className="w-full h-auto object-contain transform hover:scale-100 transition-transform duration-500"
                  loading="lazy"
                  itemProp="image"
                />
                
                {/* Floating badges */}
                <div className="absolute -top-4 -left-4 bg-white rounded-xl p-3 shadow-lg border animate-float">
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-600">30min</div>
                    <div className="text-xs text-gray-600">Service moyen</div>
                  </div>
                </div>

                <div className="absolute -bottom-4 -right-4 bg-white rounded-xl p-3 shadow-lg border animate-float-delayed">
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-600">Gratuit</div>
                    <div className="text-xs text-gray-600">Déplacement</div>
                  </div>
                </div>

                <div className="absolute top-1/4 -right-6 bg-white rounded-xl p-3 shadow-lg border animate-float">
                  <div className="text-center">
                    <div className="text-lg font-bold text-purple-600">24/7</div>
                    <div className="text-xs text-gray-600">Disponible</div>
                  </div>
                </div>
              </div>

              {/* Background decoration */}
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-400/20 to-indigo-400/20 rounded-3xl blur-xl -z-10" />
            </div>

            {/* Mobile Version - Smaller Image */}
            <div className="lg:hidden mt-8">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">500+</div>
                    <div className="text-sm text-gray-600">Clients satisfaits</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">4.9/5</div>
                    <div className="text-sm text-gray-600">Note moyenne</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
            {/* CTA Button */}
            <div className={`transition-all duration-1000 delay-700 flex justify-center mt-8 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}>
                  <a
                href="https://reservation.faucon-bleu.ma/reservation"
                target="_blank"
                rel="noopener noreferrer"
                className=" mx-auto  group inline-flex items-center justify-center w-auto 
                sm:w-auto px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 
                 scale-125
                text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-150 hover:shadow-xl"
                itemProp="url"
              >
                <img 
                  src="/images/car_small.png" 
                  alt="Service mobile" 
                  className="w-16 h-8 mr-3 transition-transform group-hover:scale-110"
                  loading="lazy"
                />
                <span>Réserver Atelier Mobile</span>
                <ArrowRight className="ml-3 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
            </div>

        {/* Bottom Stats Section */}
        <div className={`mt-20 transition-all duration-1000 delay-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
              <div className="space-y-2">
                <div className="text-3xl font-bold text-blue-600">500+</div>
                <div className="text-sm text-gray-600">Vidanges réalisées</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-green-600">4.9/5</div>
                <div className="text-sm text-gray-600">Satisfaction client</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-purple-600">30min</div>
                <div className="text-sm text-gray-600">Temps moyen</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-orange-600">0 DH</div>
                <div className="text-sm text-gray-600">Frais déplacement</div>
              </div>
            </div>
          </div>
        </div>
        
      </div>

      {/* Structured Data */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Atelier mobile - Vidange à domicile",
            "description": "Service de vidange automobile à domicile. Technicien équipé se déplace chez vous avec huiles Shell Helix et filtres de qualité.",
            "provider": {
              "@type": "AutoRepair",
              "name": "Faucon Bleu",
              "url": "https://faucon-bleu.ma"
            },
            "areaServed": {
              "@type": "City",
              "name": "Casablanca",
              "addressCountry": "MA"
            },
            "serviceType": "Mobile Oil Change",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Services atelier mobile",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Vidange à domicile",
                    "description": "Service de vidange mobile pour particuliers et professionnels"
                  }
                }
              ]
            },
            "audience": [
              {
                "@type": "Audience",
                "audienceType": "Particuliers pressés"
              },
              {
                "@type": "Audience", 
                "audienceType": "Conducteurs professionnels"
              },
              {
                "@type": "Audience",
                "audienceType": "Entreprises avec flotte automobile"
              }
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "500"
            }
          })
        }}
      />

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(5deg); }
          66% { transform: translateY(-5px) rotate(-5deg); }
        }
        
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-8px) rotate(3deg); }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: float-delayed 3s ease-in-out infinite 1.5s;
        }
      `}</style>
    </section>
  );
};

export default MobileServiceSection;